import { GraphQLSchema } from 'graphql';

import { assertUnreachable } from 'src/lib/assertUnreachable';

import {
  FieldShape,
  VariableShape,
  VariableShapes,
  getVariableShapes,
} from '../helpers/getVariableShapes/getVariableShapes';

type SchemaShape =
  | {
      type?: VariableShape['kind'];
      title?: FieldShape['type'];
      description?: FieldShape['documentation'];
      enum?: string[];
      items?: SchemaShape;
      properties?: Record<string, SchemaShape>;
      required?: Array<string>;
      additionalProperties?: false | SchemaShape;
      $ref?: string;
    }
  | { anyOf: SchemaShape[] }
  | { type: 'null' };

function makeNullable(schema: SchemaShape): SchemaShape {
  return { anyOf: [{ type: 'null' }, schema] };
}

function convertVariableShapesToSchema(shapes: VariableShapes[]) {
  const properties: Record<string, SchemaShape> = {};
  const definitions: Record<string, SchemaShape | undefined> = {};
  const variableCache: Record<string, Record<string, SchemaShape> | undefined> =
    {};
  function convertVariableShapeToSchema(
    shape: FieldShape | VariableShape,
    depth: number,
  ): SchemaShape {
    const title = 'type' in shape ? shape.type : undefined;
    const description =
      'documentation' in shape ? shape.documentation : undefined;

    // Don't consider riduculously complicated inputs
    if (depth > 25) {
      return { title, description };
    }

    switch (shape.kind) {
      case 'boolean':
      case 'string':
      case 'number':
        return { type: shape.kind, title, description };
      case 'undefined':
        return { title, description };
      case 'enum':
        return { type: 'string', title, description, enum: shape.enumValues };
      case 'array':
        return {
          anyOf: [
            {
              type: 'array',
              title,
              description,
              items: convertVariableShapeToSchema(shape.innerType, depth + 1),
            },
            convertVariableShapeToSchema(shape.innerType, depth + 1),
          ],
        };
      case 'object': {
        const existing = variableCache[shape.key];
        if (existing) {
          definitions[shape.key] = {
            type: 'object',
            title,
            description,
            properties: existing,
          };
          return { $ref: `#/definitions/${shape.key}` };
        }
        const objectProperties: Record<string, SchemaShape> = {};
        const requiredProperties: Array<string> = [];
        variableCache[shape.key] = objectProperties;
        Object.entries(shape.fields).forEach(([key, field]) => {
          const { nonNull } = field;
          const fieldShape = convertVariableShapeToSchema(field, depth + 1);
          objectProperties[key] = nonNull
            ? fieldShape
            : makeNullable(fieldShape);
          if (nonNull) {
            requiredProperties.push(key);
          }
        });
        return {
          type: 'object',
          title,
          description,
          properties: objectProperties,
          required: requiredProperties,
          additionalProperties: false,
        };
      }
      default:
        return assertUnreachable(shape);
    }
  }

  shapes.forEach((shape) =>
    Object.entries(shape.fields).forEach(([fieldName, field]) => {
      const schema = convertVariableShapeToSchema(field, 0);
      const schemaOrNull = field.nonNull ? schema : makeNullable(schema);
      const fieldProperties = properties[fieldName];
      if (fieldProperties) {
        if ('anyOf' in fieldProperties) {
          properties[fieldName] = {
            anyOf: [schemaOrNull, ...fieldProperties.anyOf],
          };
        } else {
          properties[fieldName] = {
            anyOf: [schemaOrNull, fieldProperties],
          };
        }
      } else {
        properties[fieldName] = schemaOrNull;
      }
    }),
  );
  return {
    type: 'object',
    properties,
    definitions,
    additionalProperties: false,
    required: Object.keys(properties),
  };
}

export function computeJsonSchema(
  schema: GraphQLSchema,
  operation: string,
): Record<string, unknown> | undefined {
  const allVariableShapes = getVariableShapes(schema, operation);
  if (allVariableShapes === null) {
    return undefined;
  }
  return convertVariableShapesToSchema(allVariableShapes);
}
